import { useTranslation } from 'next-i18next';
import { ReactNode, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../../../new-components/mobile/Modal';
import { logEvent } from '../../../../new-lib/analytics';
import { EntityTypes } from '../../../../new-lib/constants/common';
import { EntityDetailsTypes, Image } from '../../../../new-lib/types/entity';
import { setSelectedCompound } from '../../../../redux/slices/mapSlice';
import { DefaultMapEntity } from '../../../map/types';
import Map from '../../../../new-components/map/Map';
import QuickView from '../../../../new-components/display-system/QuickView';
interface TabsContainerProps {
  entityId?: number;
  entityType: EntityTypes;
  entityName?: string;
  masterPlanImage: string;
  floorPlanImages?: Image[];
  mapData: DefaultMapEntity;
  onSahelMap?: boolean;
  entityImages?: string[];
}

interface TabProps {
  icon: ReactNode;
  label: string;
  clickHandler: () => void;
}

const Tab = ({ icon, label, clickHandler }: TabProps) => {
  return (
    <TabContainer onClick={clickHandler} className="tab-wrapper">
      <button className="icon-wrapper">{icon}</button>
      <span className="tab-title">{label}</span>
    </TabContainer>
  );
};

const EntityTabs = ({
  masterPlanImage,
  mapData,
  entityImages
}: TabsContainerProps): JSX.Element => {
  const { t } = useTranslation(['entity']);
  const [detailType, setDetailType] = useState<EntityDetailsTypes>();
  const [showGallery, setShowGallery] = useState(false);

  const [showDetail, setShowDetail] = useState(false);
  const [GalleryComponent, setGalleryComponent] = useState<any>(null);
  const router = useRouter();
  useEffect(() => {
    if (showDetail && !GalleryComponent) {
      const Gallery = dynamic(
        () => import('../../../../new-components/Gallery')
      );
      setGalleryComponent(Gallery);
    }

    if (showDetail) {
      document
        .querySelector('.sticky-details-wrapper')
        ?.classList.add('lower-z-index');
    } else {
      document
        .querySelector('.sticky-details-wrapper')
        ?.classList.remove('lower-z-index');
    }

    return () => {
      setGalleryComponent(null);
      document.documentElement?.classList?.remove('fslightbox-open');
    };
  }, [showDetail]);

  const dispatch = useDispatch();
  const handleMapAction = () => {
    if (mapData?.geometry?.coordinates) {
      dispatch(setSelectedCompound(mapData));
      router.push(`/search?view=map`);
    } else {
      handleShowDetail(EntityDetailsTypes.MAP);
    }
  };
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const handleShowDetail = (detailType?: EntityDetailsTypes) => {
    setShowDetail(!showDetail);
    setDetailType(detailType);
    logEvent.click(`${detailType}_click`, `compoundDetailsSection`, 'other');
  };

  const getImages = () => {
    if (detailType === EntityDetailsTypes.GALLERY) return entityImages;
    return [masterPlanImage];
  };

  return (
    <Container data-test="entity-tabs">
      {masterPlanImage && (
        <Tab
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="35"
              height="34"
              viewBox="0 0 35 34"
              fill="none"
            >
              <path
                d="M3.33325 12.7497V9.20801C3.33325 5.68051 6.18075 2.83301 9.70825 2.83301H21.7499"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M31.6667 12.75V24.7917C31.6667 28.3192 28.8192 31.1667 25.2917 31.1667H13.25"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.2499 31.1667H9.70825C6.18075 31.1667 3.33325 28.3192 3.33325 24.7917V12.75"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M27.0485 3.78367L21.1694 9.6627C20.9427 9.88937 20.7302 10.3286 20.6735 10.6403L20.3477 12.8927C20.2343 13.7002 20.801 14.2811 21.6085 14.1536L23.861 13.8278C24.1727 13.7853 24.6118 13.5587 24.8385 13.332L30.7177 7.45279C31.7235 6.44696 32.2052 5.25699 30.7177 3.76949C29.2444 2.29616 28.0685 2.76367 27.0485 3.78367Z"
                stroke="#015C9A"
                stroke-width="2"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M26.2126 4.61914C26.7085 6.40414 28.1109 7.79244 29.8959 8.30244"
                stroke="#015C9A"
                stroke-width="2"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.25 2.83301V31.1663"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M3.33325 12.75H13.2499"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.25 22.667H31.6667"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          }
          label={t('master_plan')}
          clickHandler={() => {
            handleShowDetail(EntityDetailsTypes.MASTER_PLAN);
          }}
        />
      )}
      {mapData?.lat && mapData?.long && (
        <Tab
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="41"
              height="40"
              viewBox="0 0 41 40"
              fill="none"
            >
              <path
                d="M13.0369 7.63965V26.722"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M23.5613 11.4834V31.1235"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M29.1541 29.3447L24.9265 31.7706C24.1779 32.1963 22.9302 32.2257 22.1523 31.844L14.446 27.9835C13.668 27.5872 12.4203 27.6312 11.6717 28.0569L8.22218 30.0239C5.81488 31.4037 3.83325 30.2587 3.83325 27.4698V13.1874C3.83325 12.2626 4.49379 11.1177 5.3158 10.648L11.6717 7.00766C12.4203 6.58198 13.668 6.55262 14.446 6.93427L22.1523 10.7801C22.9302 11.1764 24.1779 11.1324 24.9265 10.7067L28.376 8.73975C30.7833 7.35995 32.765 8.50489 32.765 11.2938V17.6259"
                stroke="#015C9A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M27.0391 21.7232C28.2134 16.6444 35.861 16.6444 37.0207 21.7232C37.7106 24.703 35.817 27.2277 34.173 28.7983C32.9693 29.9433 31.0758 29.9286 29.8721 28.7983C28.2428 27.2277 26.3492 24.703 27.0391 21.7232Z"
                stroke="#015C9A"
                stroke-width="2"
              />
              <path
                d="M32.054 23.0439H32.0709"
                stroke="#015C9A"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          }
          label={t('view_on_map')}
          clickHandler={handleMapAction}
        />
      )}
      <Tab
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="35"
            height="34"
            viewBox="0 0 35 34"
            fill="none"
          >
            <path
              d="M13.2504 31.1673H21.7504C28.8337 31.1673 31.6671 28.334 31.6671 21.2506V12.7506C31.6671 5.66732 28.8337 2.83398 21.7504 2.83398H13.2504C6.16707 2.83398 3.33374 5.66732 3.33374 12.7506V21.2506C3.33374 28.334 6.16707 31.1673 13.2504 31.1673Z"
              stroke="#015C9A"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M13.2496 14.1667C14.8144 14.1667 16.0829 12.8981 16.0829 11.3333C16.0829 9.76853 14.8144 8.5 13.2496 8.5C11.6848 8.5 10.4163 9.76853 10.4163 11.3333C10.4163 12.8981 11.6848 14.1667 13.2496 14.1667Z"
              stroke="#015C9A"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M4.28198 26.8455L11.2661 22.1563C12.3853 21.4055 14.0003 21.4905 15.0062 22.3546L15.4737 22.7655C16.5787 23.7146 18.3637 23.7146 19.4687 22.7655L25.362 17.708C26.467 16.7588 28.252 16.7588 29.357 17.708L31.6662 19.6913"
              stroke="#015C9A"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        }
        label={t('gallery')}
        clickHandler={() => {
          setShowGallery(!showGallery);
          handleShowDetail(EntityDetailsTypes.GALLERY);
        }}
      />
      {isMobile ? (
        <>
          {mapData && (
            <Modal
              closeFn={() => handleShowDetail()}
              isOpen={detailType === EntityDetailsTypes.MAP}
              withCloseBtn={true}
              isMap={true}
            >
              <Map mapData={mapData} name={mapData?.name ?? ''} />
            </Modal>
          )}
          {GalleryComponent &&
            detailType !== undefined &&
            detailType !== EntityDetailsTypes.MAP && (
              <GalleryComponent
                show={showGallery}
                sources={getImages()}
                key={detailType}
              />
            )}
        </>
      ) : (
        <QuickView isOpen={showDetail} setIsOpen={setShowDetail}>
          {detailType == EntityDetailsTypes.MAP && (
            <Map
              mapData={{ long: mapData?.long, lat: mapData?.lat }}
              name={mapData?.name}
            />
          )}
          {detailType != EntityDetailsTypes.MAP && GalleryComponent && (
            <GalleryComponent
              show={showDetail}
              sources={getImages()}
              key={detailType}
            />
          )}
        </QuickView>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  .tab-wrapper {
    flex: 1;
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  button {
    width: 65px;
    height: 65px;
    outline: none;
    border-radius: 12px;
    border: 0.5px solid #e9f0f8;
    background: rgba(255, 255, 255, 0.09);
    box-shadow: 0px 0px 10px 1px rgba(184, 184, 184, 0.2);
  }

  .tab-title {
    color: #2c3138;
    font-family: materialRegular;
    font-size: 14px;
    text-transform: capitalize;
    text-align: center;
    line-height: normal;
  }
`;

export default EntityTabs;
